// 십성 기반 궁합 분석 유틸리티
import type { SajuResult } from './sajuCalculations';
import { HEAVENLY_STEMS as STEMS, EARTHLY_BRANCHES as BRANCHES, STEM_ELEMENTS, BRANCH_ELEMENTS } from './sajuCalculations';

// 십성 이름
const SIPSEONG_NAMES = ['비견', '겁재', '식신', '상관', '편재', '정재', '편관', '정관', '편인', '정인'];

// 십성별 특성
const SIPSEONG_TRAITS: Record<string, {
  nature: string;
  strength: string;
  weakness: string;
  loveStyle: string;
  careerFit: string[];
  moneyStyle: string;
}> = {
  비견: {
    nature: '자립심 강하고 독립적',
    strength: '자기 주관 뚜렷, 혼자서도 잘함',
    weakness: '고집 셈, 타인 의견 수용 어려움',
    loveStyle: '자유로운 연애 선호, 구속 싫어함',
    careerFit: ['프리랜서', '창업', '전문직', '자영업'],
    moneyStyle: '내 돈은 내가 관리, 경제적 자립 중시',
  },
  겁재: {
    nature: '경쟁심 강하고 활동적',
    strength: '행동력, 추진력, 도전 정신',
    weakness: '충동적, 경쟁으로 인한 소모',
    loveStyle: '열정적이지만 질투심 있음',
    careerFit: ['영업', '마케팅', '투자', '스포츠'],
    moneyStyle: '벌기도 잘하지만 쓰기도 잘함',
  },
  식신: {
    nature: '여유롭고 관대하며 낙천적',
    strength: '창의력, 표현력, 인심 좋음',
    weakness: '게으름, 의지 박약, 지나친 낙관',
    loveStyle: '로맨틱하고 감성적, 데이트 즐김',
    careerFit: ['예술', '디자인', '요리', '기획', '교육'],
    moneyStyle: '돈보다 행복 우선, 여유롭게 씀',
  },
  상관: {
    nature: '재능 있고 자기표현 강함',
    strength: '재주, 개성, 매력',
    weakness: '자기중심적, 반항심, 변덕',
    loveStyle: '짜릿한 연애 선호, 지루함 못 참음',
    careerFit: ['연예', '방송', '작가', '디자인', '기술직'],
    moneyStyle: '재능으로 벌지만 변동 심함',
  },
  편재: {
    nature: '기회 포착 능력, 모험심',
    strength: '수익 창출, 사업 능력, 네트워킹',
    weakness: '투기적, 안정성 부족, 욕심',
    loveStyle: '밀당의 달인, 자극적인 관계 선호',
    careerFit: ['사업', '투자', '부동산', '무역', '판매'],
    moneyStyle: '큰 돈을 벌기도, 잃기도 함',
  },
  정재: {
    nature: '안정적, 계획적, 현실적',
    strength: '저축, 관리, 꾸준함',
    weakness: '융통성 부족, 인색할 수 있음',
    loveStyle: '안정적인 관계 선호, 배려심 깊음',
    careerFit: ['회계', '금융', '공무원', '관리직', '행정'],
    moneyStyle: '꾸준히 모으는 타입, 안정 추구',
  },
  편관: {
    nature: '카리스마 있고 리더십 강함',
    strength: '추진력, 결단력, 책임감',
    weakness: '독선적, 압박감, 융통성 부족',
    loveStyle: '리드하는 연애, 보호본능 발동',
    careerFit: ['경영', '관리', '군경', '의료', '법조'],
    moneyStyle: '큰 그림 그리지만 리스크도 큼',
  },
  정관: {
    nature: '원칙적, 책임감 있고 신뢰감',
    strength: '성실함, 인정받음, 안정적',
    weakness: '보수적, 융통성 부족, 스트레스 내성',
    loveStyle: '진지한 연애, 결혼 생각하고 만남',
    careerFit: ['공무원', '대기업', '전문직', '교육', '법조'],
    moneyStyle: '안정적 수입, 보수적 투자',
  },
  편인: {
    nature: '독특한 통찰력, 직관력',
    strength: '창의성, 분석력, 전문성',
    weakness: '내성적, 불안감, 과도한 생각',
    loveStyle: '깊은 유대감 중시, 소수의 진한 관계',
    careerFit: ['연구', '기획', '컨설팅', '예술', '개발'],
    moneyStyle: '전문성으로 버는 타입, 변동 있음',
  },
  정인: {
    nature: '배려심 깊고 지혜로움',
    strength: '학습력, 지식, 후원받음',
    weakness: '의존적, 게으름, 현실 도피',
    loveStyle: '포근한 관계, 정서적 교류 중시',
    careerFit: ['교육', '연구', '상담', '행정', '기획'],
    moneyStyle: '안정적이지만 큰 부는 어려움',
  },
};

// 두 십성의 궁합 점수 (0-100)
const SIPSEONG_COMPATIBILITY: Record<string, Record<string, number>> = {
  비견: { 비견: 70, 겁재: 75, 식신: 80, 상관: 75, 편재: 65, 정재: 70, 편관: 60, 정관: 65, 편인: 85, 정인: 80 },
  겁재: { 비견: 75, 겁재: 65, 식신: 75, 상관: 80, 편재: 70, 정재: 65, 편관: 75, 정관: 70, 편인: 75, 정인: 70 },
  식신: { 비견: 80, 겁재: 75, 식신: 70, 상관: 75, 편재: 85, 정재: 80, 편관: 60, 정관: 65, 편인: 70, 정인: 75 },
  상관: { 비견: 75, 겁재: 80, 식신: 75, 상관: 65, 편재: 80, 정재: 75, 편관: 65, 정관: 60, 편인: 75, 정인: 70 },
  편재: { 비견: 65, 겁재: 70, 식신: 85, 상관: 80, 편재: 70, 정재: 75, 편관: 85, 정관: 80, 편인: 65, 정인: 70 },
  정재: { 비견: 70, 겁재: 65, 식신: 80, 상관: 75, 편재: 75, 정재: 75, 편관: 80, 정관: 85, 편인: 70, 정인: 75 },
  편관: { 비견: 60, 겁재: 75, 식신: 60, 상관: 65, 편재: 85, 정재: 80, 편관: 65, 정관: 75, 편인: 70, 정인: 75 },
  정관: { 비견: 65, 겁재: 70, 식신: 65, 상관: 60, 편재: 80, 정재: 85, 편관: 75, 정관: 70, 편인: 75, 정인: 80 },
  편인: { 비견: 85, 겁재: 75, 식신: 70, 상관: 75, 편재: 65, 정재: 70, 편관: 70, 정관: 75, 편인: 70, 정인: 85 },
  정인: { 비견: 80, 겁재: 70, 식신: 75, 상관: 70, 편재: 70, 정재: 75, 편관: 75, 정관: 80, 편인: 85, 정인: 75 },
};

// 십성 계산 (일간 기준)
function calculateSipseong(dayStemIdx: number, targetStemIdx: number): string {
  const diff = (targetStemIdx - dayStemIdx + 10) % 10;
  return SIPSEONG_NAMES[diff];
}

// 오행 궁합 분석 결과
export interface SipseongGoonghapResult {
  personA: {
    name: string;
    daySipseong: string;
    traits: typeof SIPSEONG_TRAITS[string];
    elements: { stem: string; branch: string };
  };
  personB: {
    name: string;
    daySipseong: string;
    traits: typeof SIPSEONG_TRAITS[string];
    elements: { stem: string; branch: string };
  };
  compatibility: {
    score: number;
    level: 'excellent' | 'good' | 'normal' | 'challenging';
    summary: string;
    details: string[];
  };
  chemistry: {
    attraction: number;
    stability: number;
    growth: number;
    description: string;
  };
  advice: {
    strongPoint: string;
    watchOut: string;
    tip: string;
  };
}

// 십성 기반 궁합 분석
export function analyzeSipseongGoonghap(
  sajuA: SajuResult,
  sajuB: SajuResult,
  nameA: string,
  nameB: string
): SipseongGoonghapResult {
  const dayA = sajuA.dayPillar;
  const dayB = sajuB.dayPillar;

  // 각자의 일간 십성
  const sipseongA = calculateSipseong(dayA.stemIdx, dayB.stemIdx);
  const sipseongB = calculateSipseong(dayB.stemIdx, dayA.stemIdx);

  // 궁합 점수
  const score = SIPSEONG_COMPATIBILITY[sipseongA]?.[sipseongB] ?? 70;

  // 레벨 판정
  let level: SipseongGoonghapResult['compatibility']['level'];
  if (score >= 80) level = 'excellent';
  else if (score >= 70) level = 'good';
  else if (score >= 60) level = 'normal';
  else level = 'challenging';

  // 요약 메시지
  const summaries: Record<string, string> = {
    excellent: `${nameA}님과 ${nameB}님은 십성적으로 최고의 궁합이에요. 서로의 부족한 부분을 자연스럽게 채워주는 관계예요.`,
    good: `${nameA}님과 ${nameB}님은 십성적으로 꽤 잘 맞는 편이에요. 서로를 이해하고 배려하면 더 좋아질 수 있어요.`,
    normal: `${nameA}님과 ${nameB}님은 십성적으로 보통 궁합이에요. 노력에 따라 충분히 좋은 관계가 될 수 있어요.`,
    challenging: `${nameA}님과 ${nameB}님은 십성적으로 도전적인 궁합이에요. 서로의 차이를 인정하고 존중하는 것이 중요해요.`,
  };

  // 상세 분석
  const details: string[] = [];
  const traitsA = SIPSEONG_TRAITS[sipseongA];
  const traitsB = SIPSEONG_TRAITS[sipseongB];

  // 성향 분석
  details.push(`${nameA}님은 ${traitsA.nature} 성향이에요.`);
  details.push(`${nameB}님은 ${traitsB.nature} 성향이에요.`);

  // 연애 스타일 비교
  if (sipseongA === sipseongB) {
    details.push('두 분의 연애 스타일이 비슷해서 서로 잘 이해할 수 있어요.');
  } else {
    details.push(`${nameA}님은 ${traitsA.loveStyle}`);
    details.push(`${nameB}님은 ${traitsB.loveStyle}`);
  }

  // 화학작용 계산
  const attraction = Math.round((score + (sipseongA === '상관' || sipseongB === '상관' ? 10 : 0)) * 0.9);
  const stability = Math.round((score + (sipseongA === '정재' || sipseongB === '정재' ? 10 : 0)) * 0.85);
  const growth = Math.round((score + (sipseongA === '정인' || sipseongB === '정인' ? 8 : 0)) * 0.88);

  // 화학작용 설명
  let chemistryDesc = '';
  if (score >= 80) {
    chemistryDesc = '두 분은 만남부터가 특별한 인연이에요. 함께 있을수록 서로에게 긍정적인 영향을 주는 관계예요.';
  } else if (score >= 70) {
    chemistryDesc = '서로에게 매력을 느끼고, 함께 성장할 수 있는 관계예요. 소통이 관건이에요.';
  } else if (score >= 60) {
    chemistryDesc = '처음엔 다를 수 있지만, 알아갈수록 매력을 발견하는 관계예요.';
  } else {
    chemistryDesc = '성향 차이가 있지만, 그 차이가 오히려 서로를 배우게 만드는 관계예요.';
  }

  // 조언
  const advice = {
    strongPoint: score >= 75
      ? '서로의 부족한 부분을 자연스럽게 채워주는 시너지가 있어요.'
      : score >= 65
        ? '서로 다른 관점에서 문제를 볼 수 있어 시너지가 나요.'
        : '서로의 차이를 통해 새로운 것을 배울 기회가 많아요.',
    watchOut: score < 65
      ? '서로의 다른 의견을 존중하지 않으면 갈등이 생길 수 있어요.'
      : '익숙함을 당연하게 여기지 않고 감사하는 마음이 필요해요.',
    tip: score >= 80
      ? '이런 궁합은 흔치 않아요. 서로를 소중히 여기세요.'
      : score >= 70
        ? '정기적으로 서로의 마음을 확인하는 시간을 가지세요.'
        : '서로의 차이를 틀림이 아닌 다름으로 받아들이세요.',
  };

  return {
    personA: {
      name: nameA,
      daySipseong: sipseongA,
      traits: traitsA,
      elements: {
        stem: STEM_ELEMENTS[dayA.stemIdx],
        branch: BRANCH_ELEMENTS[dayA.branchIdx],
      },
    },
    personB: {
      name: nameB,
      daySipseong: sipseongB,
      traits: traitsB,
      elements: {
        stem: STEM_ELEMENTS[dayB.stemIdx],
        branch: BRANCH_ELEMENTS[dayB.branchIdx],
      },
    },
    compatibility: {
      score,
      level,
      summary: summaries[level],
      details,
    },
    chemistry: {
      attraction: Math.min(100, attraction),
      stability: Math.min(100, stability),
      growth: Math.min(100, growth),
      description: chemistryDesc,
    },
    advice,
  };
}

// 오행 상생상극 분석
export function analyzeElementChemistry(
  sajuA: SajuResult,
  sajuB: SajuResult,
  nameA: string,
  nameB: string
) {
  const dayA = sajuA.dayPillar;
  const dayB = sajuB.dayPillar;

  const elementA = STEM_ELEMENTS[dayA.stemIdx];
  const elementB = STEM_ELEMENTS[dayB.stemIdx];

  // 상생 관계
  const shengCycle: Record<string, string> = {
    목: '화', 화: '토', 토: '금', 금: '수', 수: '목',
  };

  // 상극 관계
  const keCycle: Record<string, string> = {
    목: '토', 토: '수', 수: '화', 화: '금', 금: '목',
  };

  let relationship: 'mutual' | 'support' | 'control' | 'controlled';
  let description: string;

  if (shengCycle[elementA] === elementB) {
    relationship = 'support';
    description = `${elementA}이 ${elementB}을(를) 돕는 상생 관계예요. ${nameA}님이 ${nameB}님을 지지해주는 구조예요.`;
  } else if (shengCycle[elementB] === elementA) {
    relationship = 'controlled';
    description = `${elementB}이 ${elementA}을(를) 돕는 상생 관계예요. ${nameB}님이 ${nameA}님을 지지해주는 구조예요.`;
  } else if (keCycle[elementA] === elementB) {
    relationship = 'control';
    description = `${elementA}이 ${elementB}을(를) 극하는 관계예요. 서로 조율이 필요해요.`;
  } else {
    relationship = 'mutual';
    description = '두 분의 오행은 중립적인 관계예요. 큰 충돌도, 특별한 시너지도 없는 무난한 조합이에요.';
  }

  return { elementA, elementB, relationship, description };
}